/**
 * Modelo de dominio del juego.
 *
 * Estas interfaces son la única fuente de verdad compartida entre el motor puro
 * (src/engine), el estado de UI (src/store) y, a partir de la Fase 2, el
 * backend. Todo lo que aquí se declara debe poder serializarse a JSON: el
 * servidor tiene que ser capaz de re-simular una partida para validarla, así que
 * en el estado no caben funciones, clases ni referencias circulares.
 */

/* -------------------------------------------------------------------------- */
/* Constantes de reglas                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Parámetros de duración y tamaño de una partida.
 *
 * Van juntos porque están acoplados: no se puede tocar uno sin mirar los otros.
 * Las cartas que se roban en una partida son `startingHand + maxTurns`, y tienen
 * que caber en el tablero, que son `3 * cardsPerZone` huecos. Si se alargan los
 * turnos sin ampliar el tablero, los últimos turnos no tienen dónde jugarse; si
 * se alarga sin ampliar el mazo, se ve el mazo entero en todas las partidas y
 * desaparece la varianza.
 */
export interface Ruleset {
  /** Turnos que dura la partida. */
  maxTurns: number;
  /** Cartas por jugador y zona. Igual en las tres: ver MAX_CARDS_PER_ZONE. */
  cardsPerZone: number;
  /** Tamaño exacto de un mazo legal. */
  deckSize: number;
  /** Cartas con las que se abre, antes del robo del turno 1. */
  startingHand: number;
  /**
   * Techo de energía por turno. Sin él, a partir del turno 7 hay más energía que
   * cartas jugables y los turnos finales se resuelven solos.
   */
  maxEnergyPerTurn: number;
  /** Tope de mano: si está llena, el robo se descarta. */
  maxHandSize: number;
}

/**
 * Configuraciones medidas en simulación (150 partidas IA contra IA cada una).
 *
 * CLASSIC_6 es la proporción de Marvel Snap: 12 huecos, 9 cartas robadas, 5 % de
 * energía sin gastar y ni un turno sin sitio donde jugar.
 *
 * LONG_10 es la versión larga. Los tres cambios que la acompañan no son
 * opcionales: con 12 huecos el 19 % de los turnos no había dónde colocar, con
 * mazo de 12 se veía el 100 % del mazo cada partida, y sin techo de energía se
 * desperdiciaba el 44 %.
 */
export const RULESETS = {
  CLASSIC_6: {
    maxTurns: 6,
    cardsPerZone: 4,
    deckSize: 12,
    startingHand: 3,
    maxEnergyPerTurn: 6,
    maxHandSize: 7,
  },
  LONG_10: {
    maxTurns: 10,
    cardsPerZone: 6,
    deckSize: 20,
    startingHand: 3,
    maxEnergyPerTurn: 6,
    maxHandSize: 7,
  },
} satisfies Record<string, Ruleset>;

/** Reglas activas. Cambiar esta línea cambia la duración de la partida. */
export const RULES: Ruleset = RULESETS.LONG_10;

/** Turnos que dura una partida. */
export const MAX_TURNS = RULES.maxTurns;
/**
 * Cartas que caben por jugador en cada zona. Es igual en las tres a propósito:
 * la rejilla es de 2 columnas, así que una capacidad distinta en una zona le
 * añadiría una fila, la haría más alta que las otras dos y desalinearía los tres
 * marcadores. Las ubicaciones se diferencian por su efecto, no por su tamaño.
 */
export const MAX_CARDS_PER_ZONE = RULES.cardsPerZone;
/** Tamaño exacto de un mazo legal. */
export const DECK_SIZE = RULES.deckSize;
/** Cartas con las que se abre la partida, antes del robo del turno 1. */
export const STARTING_HAND_SIZE = RULES.startingHand;
/** Tope de mano: si está llena, el robo se descarta. */
export const MAX_HAND_SIZE = RULES.maxHandSize;
/** Comisión que se queda la casa en cada venta del mercado (Fase 4). */
export const MARKET_FEE_RATE = 0.1;

/* -------------------------------------------------------------------------- */
/* Enumeraciones                                                              */
/* -------------------------------------------------------------------------- */

export type Rarity = 'COMMON' | 'RARE' | 'EPIC' | 'LEGENDARY';

/** Espeja cards_master.effect_type en PostgreSQL. */
export type EffectType = 'NONE' | 'ON_REVEAL' | 'PASSIVE';

/**
 * Bando dentro de una partida. Deliberadamente no es el id del usuario: el
 * motor no necesita saber quién juega, solo desde qué lado del tablero.
 */
export type PlayerSide = 'PLAYER' | 'OPPONENT';

/** Las zonas son exactamente 3 y su índice se usa como clave en todas partes. */
export type ZoneIndex = 0 | 1 | 2;

export type MatchPhase =
  /** Ambos jugadores colocan cartas en paralelo. */
  | 'PLANNING'
  /** Se están resolviendo las revelaciones; la entrada está bloqueada. */
  | 'REVEALING'
  /** Partida terminada, GameState.result tiene el desenlace. */
  | 'FINISHED';

/* -------------------------------------------------------------------------- */
/* Efectos                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Efectos de carta como datos, no como código.
 *
 * Una unión discriminada en lugar de un callback por carta permite que el mismo
 * JSON viva en cards_master y que el servidor ejecute exactamente la misma
 * lógica que el cliente. Añadir una carta nueva con un efecto ya existente no
 * requiere tocar TypeScript.
 */
export type CardEffect =
  /** Vainilla: solo aporta su poder. */
  | { kind: 'NONE' }
  /** Roba cartas al revelarse. */
  | { kind: 'DRAW_CARDS'; amount: number }
  /** Energía extra únicamente en el turno siguiente. */
  | { kind: 'GAIN_ENERGY_NEXT_TURN'; amount: number }
  /** +poder por cada carta enemiga ya presente en su zona. */
  | { kind: 'BUFF_SELF_PER_ENEMY_HERE'; amountPer: number }
  /** +poder si tienes al menos N aliados en su zona (sin contarse a sí misma). */
  | { kind: 'BUFF_SELF_IF_ALLIES_HERE'; minAllies: number; amount: number }
  /** -poder por cada aliado en su zona. Contrapartida de las cartas muy fuertes. */
  | { kind: 'DEBUFF_SELF_PER_ALLY_HERE'; amountPer: number }
  /** -poder a una carta enemiga al azar de su zona. */
  | { kind: 'DEBUFF_RANDOM_ENEMY_HERE'; amount: number }
  /** +poder a tus cartas ya reveladas en las otras dos zonas. */
  | { kind: 'BUFF_ALLIES_OTHER_ZONES'; amount: number }
  /** Aura continua: +poder a tus otras cartas de la misma zona (PASSIVE). */
  | { kind: 'AURA_BUFF_ALLIES_HERE'; amount: number };

/** Efectos de ubicación. Se aplican a las cartas de ambos bandos por igual. */
export type LocationEffect =
  | { kind: 'NONE' }
  /** Modificador de poder a toda carta jugada aquí (puede ser negativo). */
  | { kind: 'POWER_DELTA_ALL'; amount: number }
  /** Modificador condicionado al coste de la carta. */
  | {
      kind: 'POWER_DELTA_BY_COST';
      comparator: 'LTE' | 'GTE' | 'EQ';
      cost: number;
      amount: number;
    }
  /** Nadie puede jugar aquí en el turno indicado. */
  | { kind: 'LOCK_ON_TURN'; turn: number };

/* -------------------------------------------------------------------------- */
/* Catálogo                                                                   */
/* -------------------------------------------------------------------------- */

/** Arte de la carta. Hasta tener ilustraciones reales se dibuja por CSS. */
export interface CardArt {
  /** Clave del icono en el registro de components/cards/cardArt.ts. */
  icon: string;
  /** Clases Tailwind del degradado de fondo. */
  gradient: string;
}

/** Fila de cards_master: la definición inmutable de una carta. */
export interface CardMaster {
  id: string;
  name: string;
  cost: number;
  basePower: number;
  rarity: Rarity;
  effectType: EffectType;
  /** Texto que lee el jugador. Debe describir exactamente lo que hace effect. */
  effectDescription: string;
  effect: CardEffect;
  art: CardArt;
  /** Ilustración definitiva. Nulo mientras se use el arte procedural. */
  imageUrl: string | null;
  /**
   * Tirada máxima. Nulo = ilimitada.
   *
   * Es lo que convierte una carta en un objeto con valor: sin un tope, "rara"
   * es una etiqueta de color. El recuento de lo ya acuñado vive en el servidor
   * (`cards_master.minted_count`), no aquí, porque cambia con cada sobre que se
   * abre y el cliente no puede ser quien lo lleve.
   */
  maxSupply: number | null;
}

/** Una de las ubicaciones del pool que se sortea al empezar la partida. */
export interface LocationMaster {
  id: string;
  name: string;
  description: string;
  effect: LocationEffect;
  /** Clases Tailwind del fondo de la zona. */
  gradient: string;
}

/* -------------------------------------------------------------------------- */
/* Cartas en partida                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Ejemplar concreto de una carta en la colección de un jugador.
 * En la Fase 2 instanceId pasará a ser el user_cards.id de Supabase.
 */
export interface CardInstance {
  instanceId: string;
  masterId: string;
  /** 1..5. Cada nivel de fusión suma +1 al poder base. */
  level: number;
  owner: PlayerSide;
}

/**
 * Una carta en la colección del jugador, fuera de toda partida.
 *
 * Es distinta de `CardInstance` a propósito: una carta guardada no tiene bando.
 * `CardInstance.owner` solo significa algo dentro de un tablero, y meter un
 * "PLAYER" de relleno en cada fila del inventario sería mentir sobre lo que el
 * dato representa. La conversión ocurre al empezar la partida, en
 * `toMatchCard`.
 */
export interface CollectionCard {
  /** user_cards.id */
  id: string;
  masterId: string;
  level: number;
  xp: number;
  /** Las cartas de recompensa o de pase no se pueden revender. */
  isTradable: boolean;
  /**
   * Número de serie dentro de la tirada: el "#7" de "#7 de 500".
   *
   * Nulo en las cartas de tirada ilimitada, donde numerar no significa nada.
   * Se asigna al acuñarla y no cambia nunca — es la identidad del ejemplar.
   */
  serialNumber: number | null;
  acquiredAt: string;
}

/** Convierte una carta de la colección en un ejemplar para el tablero. */
export function toMatchCard(card: CollectionCard, owner: PlayerSide): CardInstance {
  return { instanceId: card.id, masterId: card.masterId, level: card.level, owner };
}

/** Origen y magnitud de un ajuste de poder, para poder explicarlo en la UI. */
export interface PowerModifier {
  amount: number;
  /** Id de la carta o ubicación responsable. */
  sourceId: string;
  sourceName: string;
}

/** Carta ya colocada en el tablero. */
export interface BoardCard extends CardInstance {
  zone: ZoneIndex;
  /**
   * Falso mientras está colocada pero pendiente de la fase de revelación.
   * El rival solo ve el reverso de las cartas no reveladas.
   */
  revealed: boolean;
  playedOnTurn: number;
  /** Orden global de colocación: fija el orden de revelación dentro de un bando. */
  playOrder: number;
  /**
   * Ajustes permanentes ya aplicados (ON_REVEAL, ubicaciones).
   * Las auras PASSIVE NO se guardan aquí: se recalculan en cada lectura para que
   * desaparezcan solas si la carta que las genera deja el tablero.
   */
  modifiers: PowerModifier[];
}

/* -------------------------------------------------------------------------- */
/* Estado de partida                                                          */
/* -------------------------------------------------------------------------- */

export interface Zone {
  index: ZoneIndex;
  location: LocationMaster;
  /** Cartas de ambos bandos. Se filtra por owner al pintar. */
  cards: BoardCard[];
}

export interface PlayerState {
  side: PlayerSide;
  name: string;
  avatarUrl: string | null;
  /** Energía disponible ahora mismo. Lo no gastado se pierde al terminar el turno. */
  energy: number;
  /** Energía con la que empezó el turno, para pintar la barra. */
  maxEnergy: number;
  /** Bonificación de un solo uso que se sumará al empezar el turno siguiente. */
  bonusEnergyNextTurn: number;
  /** Cartas pendientes de robar, ya barajadas. */
  deck: CardInstance[];
  hand: CardInstance[];
  /** Ha confirmado el turno y espera a que el rival haga lo propio. */
  isReady: boolean;
}

export interface MatchResult {
  winner: PlayerSide | 'DRAW';
  zonesWon: Record<PlayerSide, number>;
  totalPower: Record<PlayerSide, number>;
  /** Explicación legible del desempate aplicado. */
  reason: string;
}

export interface GameState {
  matchId: string;
  /** Semilla del RNG. Con ella y el registro de jugadas la partida es reproducible. */
  seed: number;
  /** Número de valores ya consumidos del RNG. Hace el azar serializable. */
  rngCursor: number;
  turn: number;
  maxTurns: number;
  phase: MatchPhase;
  zones: [Zone, Zone, Zone];
  players: Record<PlayerSide, PlayerState>;
  /** Quién revela primero este turno: el que va ganando en poder total. */
  priority: PlayerSide;
  /** Contador monótono que alimenta BoardCard.playOrder. */
  playCounter: number;
  result: MatchResult | null;
  log: string[];
}

/**
 * Un paso de la fase de revelación.
 *
 * stateAfter es una instantánea completa: la UI reproduce la secuencia
 * avanzando por los pasos, sin recalcular nada. Cuesta memoria, pero garantiza
 * que lo que se anima es exactamente lo que el motor decidió.
 */
export interface RevealStep {
  boardCardId: string;
  side: PlayerSide;
  zone: ZoneIndex;
  cardName: string;
  /** Frase para el registro de combate: "Alquimista: +1 de energía". */
  message: string;
  stateAfter: GameState;
}

/** Resultado de endTurn: el estado final y la secuencia para animarlo. */
export interface TurnResolution {
  steps: RevealStep[];
  finalState: GameState;
}

/** Devuelto por las acciones que pueden fallar por una regla del juego. */
export type ActionResult =
  | { ok: true; state: GameState }
  | { ok: false; error: string };

/* -------------------------------------------------------------------------- */
/* Metajuego (se materializa en las fases 2-4; aquí solo el contrato)          */
/* -------------------------------------------------------------------------- */

/** Fila de profiles. */
export interface Profile {
  id: string;
  username: string;
  avatarUrl: string | null;
  gold: number;
  gems: number;
  isVip: boolean;
  vipExpiresAt: string | null;
  createdAt: string;
}

export type ListingStatus = 'ACTIVE' | 'SOLD' | 'CANCELLED';

/** Fila de market_listings ya resuelta con la carta y el vendedor. */
export interface MarketListing {
  id: string;
  sellerId: string;
  sellerUsername: string;
  userCardId: string;
  card: CollectionCard;
  priceGold: number;
  status: ListingStatus;
  createdAt: string;
}

/** Fila de decks. Debe contener exactamente DECK_SIZE ids. */
export interface Deck {
  id: string;
  userId: string;
  name: string;
  cardIds: string[];
  isActive: boolean;
}
